import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { LOGO_URL } from "../lib/i18n";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Pencil, Trash2, Plus } from "lucide-react";

const empty = {
  slug: "",
  title_es: "",
  title_en: "",
  title_fr: "",
  excerpt_es: "",
  excerpt_en: "",
  excerpt_fr: "",
  content_es: "",
  content_en: "",
  content_fr: "",
  cover_image: "",
  category_es: "Noticias",
  category_en: "News",
  category_fr: "Actualités",
  published: true,
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const [posts, setPosts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | post object
  const [draft, setDraft] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user && user !== false) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const refresh = async () => {
    try {
      const [p, c] = await Promise.all([api.get("/admin/blog"), api.get("/admin/contacts")]);
      setPosts(p.data);
      setContacts(c.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  if (user === null) return <div className="pt-40 text-center font-mono text-[11px] tracking-[0.3em] uppercase text-olive-soft">…</div>;
  if (user === false) return <Navigate to="/admin/login" replace />;

  const startNew = () => {
    setDraft(empty);
    setEditing("new");
  };

  const startEdit = (p) => {
    setDraft(p);
    setEditing(p);
  };

  const closeDialog = () => setEditing(null);

  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/admin/blog", draft);
      } else {
        await api.put(`/admin/blog/${editing.id}`, draft);
      }
      toast.success(t.admin.saveSuccess);
      closeDialog();
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(t.admin.confirmDelete)) return;
    try {
      await api.delete(`/admin/blog/${p.id}`);
      toast.success(t.admin.deleteSuccess);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <main data-testid="admin-dashboard-page" className="min-h-screen pb-24 pt-32">
      <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
        <header className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="" className="h-12 w-12 object-contain" style={{ mixBlendMode: "darken" }} />
            <div>
              <div className="font-mono text-[10px] tracking-[0.35em] uppercase text-olive-soft">Admin · {user.email}</div>
              <h1 className="font-display text-3xl text-olive">{t.admin.dashboardTitle}</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="font-mono text-[11px] tracking-[0.3em] uppercase text-olive hover:text-terracotta link-underline">↩ Site</Link>
            <button onClick={logout} data-testid="admin-logout" className="font-mono text-[11px] tracking-[0.28em] uppercase border border-olive/30 px-4 py-2 hover:bg-olive hover:text-ivory transition">
              {t.admin.logout}
            </button>
          </div>
        </header>

        <Tabs defaultValue="posts" className="w-full">
          <TabsList data-testid="admin-tabs" className="bg-ivory border border-border">
            <TabsTrigger value="posts" data-testid="tab-posts">{t.admin.postsTab}</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">{t.admin.contactsTab}</TabsTrigger>
          </TabsList>

          <TabsContent value="posts" className="mt-8">
            <div className="flex justify-end mb-6">
              <Button onClick={startNew} data-testid="admin-new-post" className="bg-olive hover:bg-terracotta text-ivory rounded-none">
                <Plus size={16} className="mr-2" /> {t.admin.newPost}
              </Button>
            </div>

            {posts.length === 0 ? (
              <div className="font-serif italic text-2xl text-olive-soft">{t.admin.noPosts}</div>
            ) : (
              <div className="border border-border divide-y divide-border bg-ivory">
                {posts.map((p) => (
                  <div key={p.id} data-testid={`admin-post-${p.id}`} className="grid grid-cols-12 gap-4 items-center p-5">
                    <div className="col-span-12 md:col-span-6">
                      <div className="font-display text-xl text-olive">{p.title_es}</div>
                      <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-olive-soft mt-1">/{p.slug}</div>
                    </div>
                    <div className="col-span-6 md:col-span-3 font-mono text-[10px] tracking-[0.25em] uppercase text-olive-soft">
                      {p.category_es} · {p.published ? t.admin.publishedLabel : "draft"}
                    </div>
                    <div className="col-span-6 md:col-span-3 flex justify-end gap-2">
                      <button onClick={() => startEdit(p)} data-testid={`edit-${p.slug}`} className="p-2 border border-olive/30 hover:bg-olive hover:text-ivory transition"><Pencil size={14} /></button>
                      <button onClick={() => remove(p)} data-testid={`delete-${p.slug}`} className="p-2 border border-terracotta/40 text-terracotta hover:bg-terracotta hover:text-ivory transition"><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="contacts" className="mt-8">
            {contacts.length === 0 ? (
              <div className="font-serif italic text-2xl text-olive-soft">—</div>
            ) : (
              <div className="border border-border divide-y divide-border bg-ivory">
                {contacts.map((c) => (
                  <div key={c.id} data-testid={`contact-row-${c.id}`} className="grid grid-cols-12 gap-4 p-5 items-start">
                    <div className="col-span-12 md:col-span-3">
                      <div className="font-display text-xl text-olive">{c.name}</div>
                      <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-olive-soft mt-1">{c.email}</div>
                      {c.phone && <div className="font-mono text-[10px] text-olive-soft">{c.phone}</div>}
                    </div>
                    <div className="col-span-6 md:col-span-2 font-mono text-[10px] tracking-[0.25em] uppercase text-terracotta">
                      {c.type}
                    </div>
                    <div className="col-span-12 md:col-span-5 text-olive whitespace-pre-line">{c.message}</div>
                    <div className="col-span-6 md:col-span-2 font-mono text-[10px] text-olive-soft text-right">
                      {new Date(c.created_at).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        <Dialog open={editing !== null} onOpenChange={(o) => !o && closeDialog()}>
          <DialogContent className="max-w-3xl bg-ivory border border-border rounded-none">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl text-olive">
                {editing === "new" ? t.admin.newPost : t.admin.edit}
              </DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-2">
              <div className="col-span-2">
                <Label className="font-mono text-[10px] tracking-[0.3em] uppercase text-olive-soft">Slug</Label>
                <Input data-testid="post-slug" value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: e.target.value })} className="rounded-none" />
              </div>
              <div>
                <Label className="font-mono text-[10px] tracking-[0.3em] uppercase text-olive-soft">Título ES</Label>
                <Input data-testid="post-title-es" value={draft.title_es} onChange={(e) => setDraft({ ...draft, title_es: e.target.value })} className="rounded-none" />
              </div>
              <div>
                <Label className="font-mono text-[10px] tracking-[0.3em] uppercase text-olive-soft">Title EN</Label>
                <Input data-testid="post-title-en" value={draft.title_en} onChange={(e) => setDraft({ ...draft, title_en: e.target.value })} className="rounded-none" />
              </div>
              <div className="col-span-2">
                <Label className="font-mono text-[10px] tracking-[0.3em] uppercase text-olive-soft">Titre FR</Label>
                <Input data-testid="post-title-fr" value={draft.title_fr} onChange={(e) => setDraft({ ...draft, title_fr: e.target.value })} className="rounded-none" />
              </div>
              <div>
                <Label className="font-mono text-[10px] tracking-[0.3em] uppercase text-olive-soft">Categoría ES</Label>
                <Input value={draft.category_es} onChange={(e) => setDraft({ ...draft, category_es: e.target.value })} className="rounded-none" />
              </div>
              <div>
                <Label className="font-mono text-[10px] tracking-[0.3em] uppercase text-olive-soft">Category EN</Label>
                <Input value={draft.category_en} onChange={(e) => setDraft({ ...draft, category_en: e.target.value })} className="rounded-none" />
              </div>
              <div className="col-span-2">
                <Label className="font-mono text-[10px] tracking-[0.3em] uppercase text-olive-soft">Catégorie FR</Label>
                <Input value={draft.category_fr} onChange={(e) => setDraft({ ...draft, category_fr: e.target.value })} className="rounded-none" />
              </div>
              <div className="col-span-2">
                <Label className="font-mono text-[10px] tracking-[0.3em] uppercase text-olive-soft">Imagen (URL)</Label>
                <Input value={draft.cover_image} onChange={(e) => setDraft({ ...draft, cover_image: e.target.value })} className="rounded-none" />
              </div>
              <div>
                <Label className="font-mono text-[10px] tracking-[0.3em] uppercase text-olive-soft">Resumen ES</Label>
                <Textarea rows={2} value={draft.excerpt_es} onChange={(e) => setDraft({ ...draft, excerpt_es: e.target.value })} className="rounded-none" />
              </div>
              <div>
                <Label className="font-mono text-[10px] tracking-[0.3em] uppercase text-olive-soft">Excerpt EN</Label>
                <Textarea rows={2} value={draft.excerpt_en} onChange={(e) => setDraft({ ...draft, excerpt_en: e.target.value })} className="rounded-none" />
              </div>
              <div className="col-span-2">
                <Label className="font-mono text-[10px] tracking-[0.3em] uppercase text-olive-soft">Résumé FR</Label>
                <Textarea rows={2} value={draft.excerpt_fr} onChange={(e) => setDraft({ ...draft, excerpt_fr: e.target.value })} className="rounded-none" />
              </div>
              <div>
                <Label className="font-mono text-[10px] tracking-[0.3em] uppercase text-olive-soft">Contenido ES</Label>
                <Textarea rows={6} value={draft.content_es} onChange={(e) => setDraft({ ...draft, content_es: e.target.value })} className="rounded-none" />
              </div>
              <div>
                <Label className="font-mono text-[10px] tracking-[0.3em] uppercase text-olive-soft">Content EN</Label>
                <Textarea rows={6} value={draft.content_en} onChange={(e) => setDraft({ ...draft, content_en: e.target.value })} className="rounded-none" />
              </div>
              <div className="col-span-2">
                <Label className="font-mono text-[10px] tracking-[0.3em] uppercase text-olive-soft">Contenu FR</Label>
                <Textarea rows={6} value={draft.content_fr} onChange={(e) => setDraft({ ...draft, content_fr: e.target.value })} className="rounded-none" />
              </div>
              <div className="col-span-2 flex items-center gap-3">
                <Switch checked={draft.published} onCheckedChange={(v) => setDraft({ ...draft, published: v })} data-testid="post-published" />
                <Label className="font-mono text-[10px] tracking-[0.3em] uppercase text-olive-soft">{t.admin.publishedLabel}</Label>
              </div>
            </div>
            <DialogFooter className="gap-3">
              <Button onClick={closeDialog} variant="outline" className="rounded-none">{t.admin.cancel}</Button>
              <Button onClick={save} disabled={saving} data-testid="post-save" className="bg-olive hover:bg-terracotta text-ivory rounded-none">
                {saving ? "…" : t.admin.saveDraft}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </main>
  );
}
